// Manufacturing → Models (BOM) mode panel.
// Primary actions: + New model (Models), Bulk import + Export (all slides).
// Top-level segmented toggle picks "Create or edit" vs "View" — View hides
// every edit affordance so an operator can browse the catalog without
// accidentally bumping anything.
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  canDeactivateManufacturingModel,
  canEditManufacturingCatalogCode,
  canPermanentlyDeleteManufacturingModel,
} from "@/lib/manufacturing/modelDeleteAccess";
import {
  canArchiveNamedVariant,
  canDeleteNamedVariant,
} from "@/lib/manufacturing/namedVariantAccess";
import { formatRecipeCreationToast } from "@/lib/manufacturing/recipeCreationToast";
import type { MfgRecipeKind } from "@/lib/manufacturing/types";
import { ManufacturingGateBanner } from "./ManufacturingGateBanner";
import { useManufacturingModelsAttachmentSection } from "./useManufacturingModelsAttachmentSection";
import {
  type ManufacturingModelsMode,
  type ManufacturingModelsWorkspace,
} from "./ManufacturingModelsModeToggle";
import { ManufacturingModelsModelsSlide } from "./ManufacturingModelsModelsSlide";
import { ManufacturingModelsPanelModals } from "./ManufacturingModelsPanelModals";
import { ManufacturingModelsRecipeToolsActions } from "./ManufacturingModelsRecipeToolsActions";
import { ManufacturingNamedVariantsPanel } from "./ManufacturingNamedVariantsPanel";
import { ManufacturingSlotLabelsPanel } from "./ManufacturingSlotLabelsPanel";
import { ManufacturingMaterialUsesPanel } from "./ManufacturingMaterialUsesPanel";
import { ManufacturingModelsWorkspaceChrome } from "./ManufacturingModelsWorkspaceChrome";
import { useManufacturingEntitlementGate } from "./useManufacturingEntitlementGate";
import { useManufacturingMaterials } from "./useManufacturingMaterials";
import { useManufacturingModelsBulkSelection } from "./useManufacturingModelsBulkSelection";
import { useManufacturingModelsListView } from "./useManufacturingModelsListView";
import { useManufacturingModelsPendingReview } from "./useManufacturingModelsPendingReview";
import { useManufacturingModelsPanelDeletes } from "./useManufacturingModelsPanelDeletes";
import { useManufacturingModelsTab } from "./useManufacturingModelsTab";
import { usePendingManufacturingPartUse } from "./usePendingManufacturingPartUse";
import { useManufacturingOrgSlotLabels } from "./useManufacturingOrgSlotLabels";

export function ManufacturingModelsPanel(props: {
  /** Jump to Production → BOM Audit (desk / plant-manager-capable roles). */
  onOpenBomAudit?: () => void;
} = {}) {
  const auth = useAuth();
  const gate = useManufacturingEntitlementGate();
  const materials = useManufacturingMaterials({ enabled: gate.enabled });

  const canDeactivateModel =
    auth?.status === "ready" && canDeactivateManufacturingModel((...r) => auth.hasRole(...r));
  const canPermanentlyDelete =
    auth?.status === "ready" && canPermanentlyDeleteManufacturingModel((...r) => auth.hasRole(...r));
  const canEditCatalogCode =
    auth?.status === "ready" && canEditManufacturingCatalogCode((...r) => auth.hasRole(...r));
  const canArchiveVariant =
    auth?.status === "ready" && canArchiveNamedVariant((...r) => auth.hasRole(...r));
  const canDeleteVariant =
    auth?.status === "ready" && canDeleteNamedVariant((...r) => auth.hasRole(...r));

  const [navTick, setNavTick] = useState(0);
  const [newSignal, setNewSignal] = useState(0);
  const m = useManufacturingModelsTab(navTick, newSignal);
  const listView = useManufacturingModelsListView(m.recipes);
  const bulk = useManufacturingModelsBulkSelection(m.recipes, listView.filtered);
  const orgSlotLabelsState = useManufacturingOrgSlotLabels(gate.enabled, navTick);
  const refreshOrgSlotLabels = useCallback(() => void orgSlotLabelsState.reload(), [orgSlotLabelsState.reload]);

  const [workspace, setWorkspace] = useState<ManufacturingModelsWorkspace>("models");
  const [usesLeaveGuard, setUsesLeaveGuard] = useState<(() => boolean) | null>(null);
  const [focusNamedVariantId, setFocusNamedVariantId] = useState<string | null>(null);
  const [deficitFocusUseId, setDeficitFocusUseId] = useState<string | null>(null);
  const [mode, setMode] = useState<ManufacturingModelsMode>("create");
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showBomSandbox, setShowBomSandbox] = useState(false);
  const [bomSandboxGroupId, setBomSandboxGroupId] = useState<string | null>(null);
  const deletes = useManufacturingModelsPanelDeletes({
    onDeactivateRecipe: m.onDeactivateRecipe,
    onPermanentlyDeleteRecipe: m.onPermanentlyDeleteRecipe,
  });
  const [dictateTranscript, setDictateTranscript] = useState<string | null>(null);
  const [dictateDefaultKind, setDictateDefaultKind] = useState<MfgRecipeKind>("assembly");
  const [importToast, setImportToast] = useState<string | null>(null);
  const isCreate = mode === "create";
  const onModelsSlide = workspace === "models";
  const onVariantsSlide = workspace === "variants";
  const onSlotsSlide = workspace === "slots";
  const onUsesSlide = workspace === "uses";

  useEffect(() => {
    if (!isCreate) bulk.clearSelection();
  }, [isCreate, bulk.clearSelection]);

  const handleBulkApplied = useCallback(async () => {
    setNavTick((t) => t + 1);
    m.reloadDetail();
  }, [m.reloadDetail]);

  const pendingReview = useManufacturingModelsPendingReview({
    enabled: gate.enabled,
    recipeCount: m.recipes.length,
    navTick,
    onAfterConfirm: () => {
      setNavTick((t) => t + 1);
      m.reloadDetail();
      bulk.clearSelection();
    },
    onError: (msg) => m.setLocalError(msg),
  });
  const { pendingCounts, refreshPendingCounts, confirmReview, handleReviewConfirmed } =
    pendingReview;

  const handleDictationComplete = useCallback((transcript: string) => {
    setDictateDefaultKind("assembly");
    setDictateTranscript(transcript);
  }, []);

  const reportModelsError = useCallback((msg: string) => {
    m.setLocalError(msg);
  }, [m.setLocalError]);

  const packageAttachments = useManufacturingModelsAttachmentSection({
    section: "packages",
    selectedId: m.selectedId,
    recipes: m.recipes,
    onError: reportModelsError,
    onSaved: refreshOrgSlotLabels,
  });

  const changeWorkspace = useCallback(
    (next: ManufacturingModelsWorkspace) => {
      if (workspace === "uses" && next !== "uses" && usesLeaveGuard && !usesLeaveGuard()) {
        return;
      }
      setWorkspace(next);
    },
    [usesLeaveGuard, workspace],
  );

  const openVariantsWorkspace = useCallback(
    (namedVariantId?: string) => {
      if (workspace === "uses" && usesLeaveGuard && !usesLeaveGuard()) return;
      setFocusNamedVariantId(namedVariantId ?? null);
      setWorkspace("variants");
    },
    [usesLeaveGuard, workspace],
  );

  const openUsesWorkspace = useCallback(() => changeWorkspace("uses"), [changeWorkspace]);
  const openPendingPartUse = useCallback((useId: string) => {
    setMode("create");
    setWorkspace("uses");
    setDeficitFocusUseId(useId);
  }, []);
  usePendingManufacturingPartUse(openPendingPartUse);

  const selectedRecipe =
    m.selectedId != null ? m.recipes.find((r) => r.id === m.selectedId) ?? null : null;

  return (
    <div className="w-full text-left space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <ManufacturingModelsWorkspaceChrome
          workspace={workspace}
          onWorkspaceChange={changeWorkspace}
          mode={mode}
          onModeChange={setMode}
          showModeToggle={onModelsSlide}
          deficitsEnabled={gate.enabled}
          deficitsRefreshTick={navTick}
          aiAssistantEnabled={gate.enabled}
          aiAssistantLoading={gate.loading}
          onAiAssistantOpenRecipe={(recipeId) => {
            setWorkspace("models");
            setMode("create");
            m.pickRecipe(recipeId);
            setNavTick((s) => s + 1);
          }}
          onAiAssistantCatalogMutated={() => setNavTick((s) => s + 1)}
          onReviewDeficitUse={(useId) => {
            if (workspace === "uses" && usesLeaveGuard && !usesLeaveGuard()) return;
            setWorkspace("uses");
            setDeficitFocusUseId(useId);
          }}
        />
        <ManufacturingModelsRecipeToolsActions
          onModelsSlide={onModelsSlide}
          isCreate={isCreate}
          gateEnabled={gate.enabled}
          gateLoading={gate.loading}
          listBusy={m.listBusy}
          detailBusy={m.detailBusy}
          onNewModel={() => setNewSignal((s) => s + 1)}
          onBulkImport={() => setShowBulkImport(true)}
          onStartFromBrief={() => {
            setBomSandboxGroupId(null);
            setShowBomSandbox(true);
          }}
          onRefresh={() => {
            gate.refresh();
            setNavTick((s) => s + 1);
          }}
          onOpenBomAudit={props.onOpenBomAudit}
        />
      </div>

      <ManufacturingGateBanner gate={gate} />
      {gate.enabled && m.localError ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {m.localError}
        </div>
      ) : null}
      {importToast ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/35 dark:text-emerald-100">
          <div className="flex items-center justify-between gap-3">
            <span>{importToast}</span>
            <button type="button" className="text-xs underline" onClick={() => setImportToast(null)}>
              Dismiss
            </button>
          </div>
        </div>
      ) : null}

      {gate.enabled && onVariantsSlide ? (
        <ManufacturingNamedVariantsPanel
          enabled={gate.enabled}
          refreshTick={navTick}
          recipes={m.recipes}
          materials={materials.materials}
          orgSlotLabels={orgSlotLabelsState.labels}
          canArchive={canArchiveVariant}
          canDelete={canDeleteVariant}
          focusNamedVariantId={focusNamedVariantId}
          onFocusedNamedVariantConsumed={() => setFocusNamedVariantId(null)}
        />
      ) : null}

      {gate.enabled && onSlotsSlide ? (
        <ManufacturingSlotLabelsPanel
          enabled={gate.enabled}
          refreshTick={navTick}
          orgSlotLabels={orgSlotLabelsState.labels}
          onLabelsChanged={refreshOrgSlotLabels}
        />
      ) : null}

      {gate.enabled && onUsesSlide ? (
        <ManufacturingMaterialUsesPanel
          enabled={gate.enabled}
          refreshTick={navTick}
          materials={materials.materials}
          recipes={m.recipes}
          onLeaveGuardChange={(guard) => setUsesLeaveGuard(() => guard)}
          focusUseId={deficitFocusUseId}
          onFocusUseApplied={() => setDeficitFocusUseId(null)}
          onDeficitsChanged={() => setNavTick((t) => t + 1)}
        />
      ) : null}

      {gate.enabled && onModelsSlide ? (
        <ManufacturingModelsModelsSlide
          isCreate={isCreate}
          m={m}
          listView={listView}
          bulk={bulk}
          materials={materials.materials}
          packageAttachments={packageAttachments}
          pendingCounts={pendingCounts}
          canEditCatalogCode={canEditCatalogCode}
          canPermanentlyDelete={canPermanentlyDelete}
          canDeactivateModel={canDeactivateModel}
          selectedRecipe={selectedRecipe}
          orgSlotLabels={orgSlotLabelsState.labels}
          onDictationComplete={handleDictationComplete}
          onRequestDeactivate={() => {
            deletes.setDeleteError(null);
            deletes.setDeleteConfirmOpen(true);
          }}
          onRequestPermanentDeleteFromList={deletes.openPermanentDelete}
          onRequestPermanentDeleteSelected={() => {
            if (selectedRecipe) deletes.openPermanentDelete(selectedRecipe);
          }}
          onDuplicate={(id) => {
            setMode("create");
            void m.beginDuplicateRecipe(id);
          }}
          onBulkApplied={handleBulkApplied}
          onConfirmReview={(ids) => void confirmReview.confirmIds(ids)}
          confirmBusy={confirmReview.busy}
          refreshPendingCounts={() => void refreshPendingCounts()}
          onSlotLabelsRefresh={refreshOrgSlotLabels}
          onOpenVariantsWorkspace={openVariantsWorkspace}
          onOpenPartUses={openUsesWorkspace}
          onReviewConfirmed={handleReviewConfirmed}
          onOpenBomSandbox={(groupId) => {
            setBomSandboxGroupId(groupId ?? null);
            setShowBomSandbox(true);
          }}
          onToast={(msg) => setImportToast(msg)}
        />
      ) : null}

      <ManufacturingModelsPanelModals
        showBulkImport={showBulkImport}
        showBomSandbox={showBomSandbox}
        bomSandboxGroupId={bomSandboxGroupId}
        materials={materials.materials}
        recipes={m.recipes}
        onCloseBulkImport={() => setShowBulkImport(false)}
        onBulkImported={(summary) => {
          setImportToast(formatRecipeCreationToast(summary, "imported"));
          setNavTick((s) => s + 1);
          void refreshPendingCounts();
        }}
        onPickRecipe={(id) => {
          setShowBulkImport(false);
          setMode("create");
          setWorkspace("models");
          m.pickRecipe(id);
        }}
        onCloseBomSandbox={() => {
          setShowBomSandbox(false);
          setBomSandboxGroupId(null);
        }}
        onBomSandboxCommitted={(recipeId) => {
          setShowBomSandbox(false);
          setBomSandboxGroupId(null);
          setImportToast("BOM started from brief — review pending matches on the recipe.");
          setNavTick((s) => s + 1);
          void refreshPendingCounts();
          setMode("create");
          setWorkspace("models");
          m.pickRecipe(recipeId);
        }}
        dictateTranscript={dictateTranscript}
        orgSlotLabels={orgSlotLabelsState.labels}
        dictateDefaultKind={dictateDefaultKind}
        onCloseDictate={() => setDictateTranscript(null)}
        onDictateSaved={(summary) => {
          setImportToast(formatRecipeCreationToast(summary, "created"));
          const newId = summary.created[0]?.recipe.id;
          setDictateTranscript(null);
          setNavTick((s) => s + 1);
          void refreshPendingCounts();
          if (newId) m.pickRecipe(newId);
        }}
        deleteConfirmOpen={deletes.deleteConfirmOpen}
        selectedRecipe={selectedRecipe}
        deleteBusy={deletes.deleteBusy}
        deleteError={deletes.deleteError}
        onCancelDeactivate={deletes.cancelDeactivate}
        onConfirmDeactivate={() => void deletes.confirmDeactivateModel()}
        permanentDeleteOpen={deletes.permanentDeleteOpen}
        permanentDeleteTarget={deletes.permanentDeleteTarget}
        permanentDeleteBlockers={deletes.permanentDeleteBlockers}
        onCancelPermanentDelete={deletes.cancelPermanentDelete}
        onConfirmPermanentDelete={() => void deletes.confirmPermanentDelete()}
      />
    </div>
  );
}
